import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import Drawer from '../../theme/components/Drawer';
import CurrencyFormatter from '../../utils/CurrencyFormatter';

/**
 * AffiliateInviteDrawer - Drawer para gerenciar convites de afiliado
 *
 * Funcionalidades:
 * - Mostra checklist de requisitos (responsável + conta bancária)
 * - Lista convites pendentes com detalhes do produto
 * - Permite aceitar/recusar convites
 * - Links diretos para cadastrar requisitos faltantes
 *
 * @param {boolean} show - Controla visibilidade
 * @param {function} onHide - Callback ao fechar
 * @param {Array} invites - Lista de convites pendentes
 * @param {Object} requirements - Status dos requisitos
 * @param {function} onAccept - Callback ao aceitar convite
 * @param {function} onReject - Callback ao recusar convite
 * @param {function} onRefresh - Callback para atualizar dados
 */
const AffiliateInviteDrawer = ({
  show,
  onHide,
  invites = [],
  requirements = null,
  onAccept,
  onReject,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [processingToken, setProcessingToken] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Verificar se pode aceitar (todos requisitos OK)
  const canAccept = requirements?.canAcceptInvites || false;

  // Handler para aceitar
  const handleAccept = async (token) => {
    if (!canAccept) {
      setFeedback({
        type: 'warning',
        message: 'Complete todos os requisitos antes de aceitar o convite.'
      });
      return;
    }

    setProcessingToken(token);
    setFeedback(null);

    try {
      const result = await onAccept(token);
      if (result.success) {
        setFeedback({ type: 'success', message: result.message });
        // Se não houver mais convites, fechar drawer após delay
        if (invites.length === 1) {
          setTimeout(() => {
            onHide();
            setFeedback(null);
          }, 2000);
        }
      } else {
        setFeedback({ type: 'danger', message: result.message });
      }
    } catch (error) {
      setFeedback({ type: 'danger', message: 'Erro ao processar. Tente novamente.' });
    } finally {
      setProcessingToken(null);
    }
  };

  // Handler para recusar
  const handleReject = async (token) => {
    setProcessingToken(token);
    setFeedback(null);

    try {
      const result = await onReject(token);
      if (result.success) {
        setFeedback({ type: 'info', message: result.message });
        // Se não houver mais convites, fechar drawer após delay
        if (invites.length === 1) {
          setTimeout(() => {
            onHide();
            setFeedback(null);
          }, 2000);
        }
      } else {
        setFeedback({ type: 'danger', message: result.message });
      }
    } catch (error) {
      setFeedback({ type: 'danger', message: 'Erro ao processar. Tente novamente.' });
    } finally {
      setProcessingToken(null);
    }
  };

  // Navegar para completar requisito
  const goToRequirement = (path) => {
    onHide();
    navigate(path);
  };

  // Formatar comissão para exibição
  const formatCommission = (rate, type) => {
    if (type === 'percentage') {
      return `${rate}%`;
    } else {
      return CurrencyFormatter.centsToFormatted(rate);
    }
  };

  // Calcular tempo restante
  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return 'Expirado';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days} dia${days > 1 ? 's' : ''} restante${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hora${hours > 1 ? 's' : ''} restante${hours > 1 ? 's' : ''}`;
    return 'Expira em breve';
  };

  return (
    <Drawer
      show={show}
      onHide={onHide}
      title="Convites de Afiliado"
      icon="mdi:account-plus"
      size="md"
    >
      {/* Feedback */}
      {feedback && (
        <div
          className={`alert alert-${feedback.type} d-flex align-items-center mb-3`}
          role="alert"
        >
          <Icon
            icon={
              feedback.type === 'success' ? 'mdi:check-circle' :
              feedback.type === 'warning' ? 'mdi:alert' :
              feedback.type === 'info' ? 'mdi:information' :
              'mdi:alert-circle'
            }
            className="me-2"
            style={{ fontSize: '20px' }}
          />
          {feedback.message}
        </div>
      )}

      {/* Seção de Requisitos */}
      <Drawer.Section title="Requisitos para ser Afiliado" icon="mdi:clipboard-check">
        {requirements ? (
          <div className="requirements-checklist">
            {/* Status geral */}
            <Drawer.InfoCard
              variant={canAccept ? 'success' : 'warning'}
              icon={canAccept ? 'mdi:check-circle' : 'mdi:alert'}
            >
              {canAccept ? (
                <span style={{ fontWeight: '500' }}>
                  Tudo pronto! Você pode aceitar convites de afiliado.
                </span>
              ) : (
                <span style={{ fontWeight: '500' }}>
                  Complete os requisitos abaixo para poder aceitar convites.
                </span>
              )}
            </Drawer.InfoCard>

            {/* Checklist de requisitos */}
            <div style={{ marginTop: '16px' }}>
              {/* Responsável */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: requirements.requirements?.responsible?.approved
                    ? 'rgba(40, 167, 69, 0.1)'
                    : 'rgba(255, 193, 7, 0.1)',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  border: `1px solid ${
                    requirements.requirements?.responsible?.approved
                      ? 'rgba(40, 167, 69, 0.3)'
                      : 'rgba(255, 193, 7, 0.3)'
                  }`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon
                    icon={
                      requirements.requirements?.responsible?.approved
                        ? 'mdi:check-circle'
                        : requirements.requirements?.responsible?.exists
                          ? 'mdi:clock-outline'
                          : 'mdi:circle-outline'
                    }
                    style={{
                      fontSize: '24px',
                      color: requirements.requirements?.responsible?.approved
                        ? '#28a745'
                        : requirements.requirements?.responsible?.exists
                          ? '#ffc107'
                          : '#6c757d'
                    }}
                  />
                  <div>
                    <span style={{ fontWeight: '500', display: 'block' }}>
                      Responsável Cadastrado
                    </span>
                    <span style={{ fontSize: '13px', color: '#6c757d' }}>
                      {requirements.requirements?.responsible?.approved
                        ? 'Aprovado'
                        : requirements.requirements?.responsible?.exists
                          ? `Aguardando aprovação (${requirements.requirements?.responsible?.status})`
                          : 'Não cadastrado'}
                    </span>
                  </div>
                </div>

                {!requirements.requirements?.responsible?.approved && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => goToRequirement('/responsibles')}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <Icon icon="mdi:arrow-right" className="me-1" />
                    Cadastrar
                  </button>
                )}
              </div>

              {/* Conta Bancária */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: requirements.requirements?.bankAccount?.approved
                    ? 'rgba(40, 167, 69, 0.1)'
                    : 'rgba(255, 193, 7, 0.1)',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  border: `1px solid ${
                    requirements.requirements?.bankAccount?.approved
                      ? 'rgba(40, 167, 69, 0.3)'
                      : 'rgba(255, 193, 7, 0.3)'
                  }`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon
                    icon={
                      requirements.requirements?.bankAccount?.approved
                        ? 'mdi:check-circle'
                        : requirements.requirements?.bankAccount?.exists
                          ? 'mdi:clock-outline'
                          : 'mdi:circle-outline'
                    }
                    style={{
                      fontSize: '24px',
                      color: requirements.requirements?.bankAccount?.approved
                        ? '#28a745'
                        : requirements.requirements?.bankAccount?.exists
                          ? '#ffc107'
                          : '#6c757d'
                    }}
                  />
                  <div>
                    <span style={{ fontWeight: '500', display: 'block' }}>
                      Conta Bancária Aprovada
                    </span>
                    <span style={{ fontSize: '13px', color: '#6c757d' }}>
                      {requirements.requirements?.bankAccount?.approved
                        ? 'Aprovado'
                        : requirements.requirements?.bankAccount?.exists
                          ? `Aguardando aprovação (${requirements.requirements?.bankAccount?.status})`
                          : 'Não cadastrada'}
                    </span>
                  </div>
                </div>

                {!requirements.requirements?.bankAccount?.approved && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => goToRequirement('/banks')}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <Icon icon="mdi:arrow-right" className="me-1" />
                    Cadastrar
                  </button>
                )}
              </div>

              {/* Recipient ID (indicador visual apenas) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: requirements.requirements?.recipient?.exists
                    ? 'rgba(40, 167, 69, 0.1)'
                    : 'rgba(108, 117, 125, 0.1)',
                  borderRadius: '8px',
                  border: `1px solid ${
                    requirements.requirements?.recipient?.exists
                      ? 'rgba(40, 167, 69, 0.3)'
                      : 'rgba(108, 117, 125, 0.3)'
                  }`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon
                    icon={
                      requirements.requirements?.recipient?.exists
                        ? 'mdi:check-circle'
                        : 'mdi:circle-outline'
                    }
                    style={{
                      fontSize: '24px',
                      color: requirements.requirements?.recipient?.exists
                        ? '#28a745'
                        : '#6c757d'
                    }}
                  />
                  <div>
                    <span style={{ fontWeight: '500', display: 'block' }}>
                      Conta de Recebimento Ativa
                    </span>
                    <span style={{ fontSize: '13px', color: '#6c757d' }}>
                      {requirements.requirements?.recipient?.exists
                        ? 'Configurado automaticamente'
                        : 'Criado após aprovação da conta bancária'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <span className="ms-2 text-muted">Verificando requisitos...</span>
          </div>
        )}
      </Drawer.Section>

      <Drawer.Divider />

      {/* Seção de Convites */}
      <Drawer.Section title={`Convites Pendentes (${invites.length})`} icon="mdi:email-outline">
        {invites.length === 0 ? (
          <Drawer.EmptyState
            icon="mdi:email-check"
            text="Nenhum convite pendente no momento"
          />
        ) : (
          <div className="invites-list">
            {invites.map((invite, index) => (
              <div
                key={invite.id}
                style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: index < invites.length - 1 ? '12px' : '0',
                  border: '1px solid #e9ecef'
                }}
              >
                {/* Header do convite */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  {/* Imagem do produto */}
                  {invite.product_image ? (
                    <img
                      src={invite.product_image}
                      alt={invite.product_name}
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                    >
                      <Icon icon="mdi:package-variant" style={{ fontSize: '28px' }} />
                    </div>
                  )}

                  {/* Info do produto */}
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'block',
                        marginBottom: '4px'
                      }}
                    >
                      {invite.product_name}
                    </span>
                    <span style={{ fontSize: '13px', color: '#6c757d', display: 'block' }}>
                      Convidado por: {invite.manager_name}
                    </span>
                  </div>
                </div>

                {/* Badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {/* Comissão */}
                  <span
                    style={{
                      background: '#28a745',
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Icon icon="mdi:cash" style={{ fontSize: '16px' }} />
                    Comissão: {formatCommission(invite.commission_rate, invite.commission_type)}
                  </span>

                  {/* Expiração */}
                  <span
                    style={{
                      background: invite.expires_at && new Date(invite.expires_at) < new Date()
                        ? '#dc3545'
                        : '#ffc107',
                      color: invite.expires_at && new Date(invite.expires_at) < new Date()
                        ? 'white'
                        : '#212529',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Icon icon="mdi:clock-outline" style={{ fontSize: '16px' }} />
                    {invite.expires_at ? getTimeRemaining(invite.expires_at) : 'Sem expiração'}
                  </span>
                </div>

                {/* Botões de ação */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-success btn-sm flex-grow-1"
                    onClick={() => handleAccept(invite.token)}
                    disabled={!canAccept || processingToken === invite.token}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    title={!canAccept ? 'Complete os requisitos acima para aceitar' : ''}
                  >
                    {processingToken === invite.token ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:check" style={{ fontSize: '18px' }} />
                        Aceitar
                      </>
                    )}
                  </button>

                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleReject(invite.token)}
                    disabled={processingToken === invite.token}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      minWidth: '100px'
                    }}
                  >
                    <Icon icon="mdi:close" style={{ fontSize: '18px' }} />
                    Recusar
                  </button>
                </div>

                {/* Aviso se não pode aceitar */}
                {!canAccept && (
                  <div
                    style={{
                      marginTop: '10px',
                      padding: '8px 12px',
                      background: 'rgba(255, 193, 7, 0.15)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#856404',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Icon icon="mdi:information" style={{ fontSize: '18px' }} />
                    Complete os requisitos acima para aceitar este convite
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Drawer.Section>
    </Drawer>
  );
};

export default AffiliateInviteDrawer;
